import React from 'react';

import * as styles from './styles';
import { Checkbox, ContainerDefault, Divider, Dropdown, FilterIcon, HoverButton } from '..';
import FormGroup from '@material-ui/core/FormGroup';

import type { PlacementType } from '../Dropdown';
import { Heading2 } from '../typography';

type OptionsMap<T extends string> = { [optionKey in T]?: string };

export interface Category<T extends string> {
  title?: string;
  options: OptionsMap<T>;
}

interface FilterDropdownProps<T extends string> {
  buttonTitle: string;
  categories: Array<Category<T>>;
  selected: Set<T>;
  disabled?: Set<T>;
  placement?: PlacementType;
  onChange: (selected: Set<T>) => void;
}

interface FilterDropdownState {
  hovered: boolean;
}

/**
 * @deprecated This component is deprecated and will be removed in the future.
 * @description Now use '@cobliteam/ui/components/*' components.
 */
export class FilterDropdown<T extends string> extends React.Component<FilterDropdownProps<T>, FilterDropdownState> {
  constructor(props: FilterDropdownProps<T>) {
    super(props);

    this.state = {
      hovered: false,
    };
  }

  private handleChange = (optionKey: T) => {
    const updatedSelected = new Set(this.props.selected);
    const isCurrentlySelected = this.props.selected.has(optionKey);
    if (isCurrentlySelected) {
      updatedSelected.delete(optionKey);
    } else {
      updatedSelected.add(optionKey);
    }

    this.props.onChange(updatedSelected);
  };

  private onHoverChange = (hovered: boolean) => {
    this.setState({ hovered });
  };

  private getButtonTitle = () => {
    const numSelected = this.props.selected.size;

    if (numSelected > 0) {
      return `${this.props.buttonTitle} (${numSelected})`;
    }

    return this.props.buttonTitle;
  };

  private renderOptions = (category: Category<T>) => (
    <FormGroup>
      {Object.keys(category.options).map((key) => (
        <Checkbox
          className={styles.checkbox}
          key={key}
          checked={this.props.selected.has(key as T)}
          onChange={() => this.handleChange(key as T)}
          value={key}
          label={category.options[key as T]}
          disabled={this.props.disabled?.has(key as T)}
        />
      ))}
    </FormGroup>
  );

  private renderDivider = (index: number) => {
    const notLastCategory = index !== this.props.categories.length - 1;
    if (notLastCategory) {
      return <Divider className={styles.divider} vertical />;
    }
  };

  private getOverlay = () => (
    <ContainerDefault className={styles.overlay}>
      {this.props.categories.map((category, index) => (
        <React.Fragment key={index}>
          <div>
            {category.title && (
              <div className={styles.title}>
                <Heading2>{category.title}</Heading2>
              </div>
            )}
            {this.renderOptions(category)}
          </div>
          {this.renderDivider(index)}
        </React.Fragment>
      ))}
    </ContainerDefault>
  );

  public render() {
    return (
      <Dropdown trigger='hover' overlay={this.getOverlay} onHoverChange={this.onHoverChange} placement={this.props.placement}>
        <HoverButton hovered={this.state.hovered}>
          <FilterIcon className={styles.icon} />
          {this.getButtonTitle()}
        </HoverButton>
      </Dropdown>
    );
  }
}
